import { Grid, IconButton, Stack, Tooltip } from '@mui/material';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import moment from 'moment';
import T from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NotifyIcon } from 'web-components';
import StyledChip from 'components/elements/StyledChip';
import GatewayTable from './GatewayTable';
import {
  GATEWAY_CONNECTED,
  GATEWAY_CONNECTION_STATUS,
  GATEWAY_CONNECTION_TIME,
  GATEWAY_NBS_SYNC_STATUS,
  GATEWAY_NOT_CONNECTED
} from '../../attrs/gateways';
import { formatLocalizedMoment } from '../../helpers/dateLocale';
import { StyledSmall } from './elements';
import { formatMachineType } from '../../helpers/utils';

const isGatewayConfigured = gateway => [true, 'true'].includes(gateway?.configured);

const GatewayTableSelector = ({ onDeleteGateway, onEditGateway, onQueryStateChange, rows }) => {
  const { t } = useTranslation();

  const getNbsSyncLabel = nbsSync =>
    t(`gateways.nbs_sync.${(nbsSync || GATEWAY_NBS_SYNC_STATUS.PENDING).toLowerCase()}`, {
      defaultValue: t('gateways.nbs_sync.pending')
    });

  const columns = [
    { id: 'serial_number', label: t('gateways.overview.gateway_id'), sortable: true },
    {
      id: 'configured',
      label: t('gateways.overview.is_configured'),
      sortable: false,
      cellRenderer: row => {
        const configured = isGatewayConfigured(row);

        return (
          <StyledChip
            className={configured ? 'configured' : 'unconfigured'}
            variant="outlined"
            style={{ border: '1px solid', cursor: 'pointer' }}
            label={configured ? t('gateways.configured') : t('gateways.not_configured')}
          />
        );
      }
    },
    {
      id: 'organization_name',
      label: t('gateways.overview.customer'),
      sortable: true,
      cellRenderer: row => row.organization_name || '---'
    },
    {
      id: 'machine_count',
      label: t('gateways.overview.machines_number'),
      sortable: false,
      cellRenderer: row => row.machine_count
    },
    {
      id: 'actual_version',
      label: t('gateways.overview.version'),
      sortable: false,
      cellRenderer: row => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {row.actual_version || '---'}
          {row.actual_version !== row.desired_version && (
            <Tooltip title={t('gateways.not_current_version')} placement="bottom" arrow>
              <span>
                <NotifyIcon
                  iconName="checkmarkFilled"
                  style={{
                    paddingLeft: '.5rem',
                    marginTop: '.5rem'
                  }}
                  fontSize="large"
                />
              </span>
            </Tooltip>
          )}
        </div>
      )
    },
    {
      id: 'type',
      label: t('gateways.overview.type'),
      sortable: false,
      cellRenderer: row => formatMachineType(row.type)
    },
    {
      id: 'last_seen',
      label: t('gateways.overview.status'),
      sortable: true,
      cellRenderer: row => {
        const lastSeen = moment((row || {}).last_seen || null);
        const lastSeenDiff = moment().diff(lastSeen, 'minutes');
        const status = lastSeenDiff <= GATEWAY_CONNECTION_TIME ? GATEWAY_CONNECTED : GATEWAY_NOT_CONNECTED;
        const formattedLastSeen = formatLocalizedMoment(row?.last_seen);
        return (
          <Grid container spacing={0} style={{ display: 'block' }}>
            <Grid item xs={12}>
              {status === 'NOT_CONNECTED' ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <NotifyIcon
                    iconName="gatewayDesconnected"
                    fontSize="medium"
                    htmlColor={GATEWAY_CONNECTION_STATUS[status].color}
                    style={{
                      marginRight: '.5rem'
                    }}
                  />
                  {t(GATEWAY_CONNECTION_STATUS[status].label)}
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <NotifyIcon
                    iconName="gatewayConnected"
                    fontSize="medium"
                    htmlColor={GATEWAY_CONNECTION_STATUS[status].color}
                    style={{
                      marginRight: '.5rem'
                    }}
                  />
                  {t(GATEWAY_CONNECTION_STATUS[status].label)}
                </div>
              )}
            </Grid>
            <Grid item xs={12}>
              <StyledSmall>{`${t('gateways.overview.last_seen')}: ${formattedLastSeen}`}</StyledSmall>
            </Grid>
            <Grid item xs={12}>
              <StyledSmall data-test-id={`gateway-nbs-sync-${row.serial_number}`}>
                {getNbsSyncLabel(row.nbs_sync)}
              </StyledSmall>
            </Grid>
          </Grid>
        );
      }
    },
    {
      id: 'actions',
      label: '',
      sortable: false,
      cellRenderer: row => {
        const isConfigured = isGatewayConfigured(row);

        if (isConfigured) {
          return null;
        }

        return (
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
            <IconButton
              color="primary"
              size="large"
              aria-label={t('gateways.crud.edit.title')}
              onClick={event => {
                event.stopPropagation();
                onEditGateway(row);
              }}
              data-test-id={`edit-gateway-button-${row.serial_number}`}
            >
              <EditOutlinedIcon sx={{ fontSize: 24 }} />
            </IconButton>
            <IconButton
              color="error"
              size="large"
              aria-label={t('gateways.crud.delete.title')}
              onClick={event => {
                event.stopPropagation();
                onDeleteGateway(row);
              }}
              data-test-id={`delete-gateway-button-${row.serial_number}`}
            >
              <DeleteForeverOutlinedIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Stack>
        );
      }
    }
  ];

  return <GatewayTable columns={columns} onQueryStateChange={onQueryStateChange} rows={rows} />;
};

GatewayTableSelector.propTypes = {
  onDeleteGateway: T.func,
  onEditGateway: T.func,
  onQueryStateChange: T.func,
  rows: T.instanceOf(Object).isRequired
};

GatewayTableSelector.defaultProps = {
  onDeleteGateway: () => null,
  onEditGateway: () => null,
  onQueryStateChange: () => null
};

export default GatewayTableSelector;
